import { useState } from "react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useLifecycle, usePause, useResume } from "@/api/hooks/use-lifecycle";
import { formatTimestamp } from "@/lib/format";
import { toUserMessage } from "@/lib/errors";

const PAUSE_MESSAGE =
  "Your requests will be forwarded to your provider exactly as sent: nothing is optimized and no Savings fee is " +
  "charged until you resume. Pausing does not stop a clock that is already running, such as the Free week, " +
  "Payment grace or a Benchmark.";

/** Settings: the Pause switch. Admin Members pause (after confirming) and
 * resume (in one click); everyone else sees the state. */
export function PauseControl() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const lifecycle = useLifecycle();
  const pause = usePause();
  const resume = useResume();
  const [confirming, setConfirming] = useState(false);

  const paused = lifecycle.data?.reason === "paused";
  const benchmark = lifecycle.data?.reason === "benchmark";
  const error = pause.error ?? resume.error;

  return (
    <div className="rounded-lg border border-line-subtle bg-surface p-5 space-y-3">
      <div className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-sm font-medium text-fg">Optimization</h2>
          {lifecycle.data && (
            <p data-testid="optimization-state" className="text-sm text-fg-muted">
              {paused ? (
                <>
                  Paused since{" "}
                  <span className="font-mono">{formatTimestamp(lifecycle.data.paused_since)}</span>. Requests go to
                  your provider exactly as sent.
                </>
              ) : benchmark ? (
                <>
                  Benchmark running until{" "}
                  <span className="font-mono">{formatTimestamp(lifecycle.data.benchmark_ends_at)}</span>. Your requests
                  go to your provider as sent while Phantm measures what it would save.
                </>
              ) : lifecycle.data.benchmark_armed ? (
                "Your Benchmark starts with your first request. Until it ends, your requests go to your provider as sent."
              ) : (
                "On. Pause it at any time if something looks wrong; requests then go to your provider exactly as sent."
              )}
            </p>
          )}
        </div>
        {lifecycle.data && isAdmin && (
          paused ? (
            <button
              type="button"
              onClick={() => resume.mutate()}
              disabled={resume.isPending}
              className="shrink-0 rounded-md bg-electric-blue px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-electric-blue/90 disabled:opacity-40"
            >
              Resume optimization
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="shrink-0 rounded-md border border-line px-4 py-2 text-sm font-medium text-fg transition-colors hover:bg-surface-2"
            >
              Pause optimization
            </button>
          )
        )}
      </div>

      {lifecycle.data && !isAdmin && (
        <p className="text-xs text-fg-faint">Only admins can pause or resume optimization.</p>
      )}

      {error && (
        <div className="rounded-md border border-block-red/30 bg-block-red/10 px-3 py-2 text-sm text-block-red">
          {toUserMessage(error)}
        </div>
      )}

      {confirming && (
        <ConfirmDialog
          title="Pause optimization?"
          message={PAUSE_MESSAGE}
          confirmLabel="Pause"
          variant="primary"
          isPending={pause.isPending}
          onConfirm={() => pause.mutate(undefined, { onSettled: () => setConfirming(false) })}
          onCancel={() => setConfirming(false)}
        />
      )}
    </div>
  );
}
